import { defineDocument } from '../../schema';
export const gameWantedDocument = defineDocument({
    id: 'game:wanted',
    kind: 'game',
    slug: 'wanted',
    route: '/discover/games/wanted',
    seo: {
        title: 'Discover Wanted',
        description: 'Sketch a secret suspect wanted for an absurd crime, race to guess who it is, then vote the real charge from a lineup of decoys in this drawing party game.',
        imageAsset: 'coverWanted',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Discover Game',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Wanted',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Sketch a secret suspect wanted for an absurd crime while detectives race to crack the case.',
            },
        ],
        imageAsset: 'coverWanted',
        imageAlt: 'Wanted game cover',
        showDescription: true,
        background: 'blurred-image',
    },
    aside: {
        showToc: true,
    },
    sections: [
        {
            id: 'game-description',
            title: [
                {
                    type: 'text',
                    text: 'Game Description',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'The sheriff needs help catching some truly heinous criminals.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Each round, one player secretly pairs a suspect with an absurd crime and draws it live on a WANTED poster. Everyone else plays detective, racing to identify the suspect from the sketch. Faster correct guesses earn more points, and the artist scores every time a detective cracks the case.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "Once the suspect is revealed, the whole precinct files a charge, picking the real crime from a lineup of look-alikes to earn extra points. Score the most in this free-for-all to prove you're one of Airhead's finest officers.",
                        },
                    ],
                },
            ],
        },
        {
            id: 'facts',
            title: [
                {
                    type: 'text',
                    text: 'Facts',
                },
            ],
            blocks: [
                {
                    type: 'facts',
                    items: [
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Players',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '3-8 players',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Game Type',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Free-For-All',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Mechanics',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Drawing, Deduction, Free Input, Selection, Voting',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Average Duration',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '~20 minutes',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Release Date',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'July 11, 2026',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Cost',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '1 Loon',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'clips',
            title: [
                {
                    type: 'text',
                    text: 'Clips',
                },
            ],
            widgets: [
                {
                    type: 'clipScroller',
                    gameId: '6a4445679a8400bf1b9aae8d',
                },
            ],
        },
        {
            id: 'play-sequence',
            title: [
                {
                    type: 'text',
                    text: 'Play Sequence',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Wanted is a competitive drawing game where players sketch WANTED posters and race to identify suspects and crimes. The following steps repeat until all players have taken a turn as the Sketch Artist.',
                        },
                    ],
                },
                {
                    type: 'list',
                    ordered: true,
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'There is one Sketch Artist per round. They secretly pick a suspect and an absurd crime committed.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'The artist has a time limit to sketch the suspect committing the crime. Detectives (all other players) see the drawing as it comes together and race to identify the suspect.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'After the drawing time is up, the suspect is revealed. Detectives then must pick the crime from a lineup of decoys.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'The true crime is revealed and points are awarded for the round.',
                            },
                        ],
                    ],
                },
            ],
        },
        {
            id: 'scoring-winning',
            title: [
                {
                    type: 'text',
                    text: 'Scoring & Winning',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Earning points:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Detectives earn points by solve order: identifying the suspect faster earns more.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Detectives earn a +50 point bonus for correctly guessing the crime.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Artists bank points for every detective who correctly identifies the suspect and crime.',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'The game ends once everyone has taken a turn as the Sketch Artist. The player with the most points wins. Tied players share the victory.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'generated-content',
            title: [
                {
                    type: 'text',
                    text: 'Generated Content',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Players:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'The live poster sketches',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Suspect guesses',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Poppy AI:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Suspect candidates',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Absurd crime candidates',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Decoy crimes for the crime committed',
                            },
                        ],
                    ],
                },
            ],
        },
        {
            id: 'other-notes',
            title: [
                {
                    type: 'text',
                    text: 'Other Notes',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Brain power required: medium. Deduction under time pressure.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Popular with: artists, classic party-game fans, competitive guessers',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Read more: ',
                        },
                        {
                            type: 'link',
                            href: '/blog/wild-things-wanted',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Wild Things Wanted blog post',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'songs',
            title: [
                {
                    type: 'text',
                    text: 'Songs',
                },
            ],
            widgets: [
                {
                    type: 'songTable',
                    gameId: '6a4445679a8400bf1b9aae8d',
                },
            ],
        },
    ],
});
//# sourceMappingURL=wanted.js.map